/* ==========================================================================
   Fifth Room — Google reviews section (fr-google-reviews)

   Progressive enhancement ONLY. With JavaScript disabled the section is still
   complete and usable: full review text is visible, and the card row is a
   native overflow scroller you can swipe or drag. This file adds three things
   on top:

     1. Read more / Show less — long reviews (one of ours runs to 860+ chars)
        are clamped so cards stay a sane height. The clamp class is added HERE,
        not in the markup, so no-JS users never get text cut off with no way to
        expand it.
     2. Avatar fallback — Google's lh3.googleusercontent.com profile URLs expire
        over time. On error we swap in an initial chip rather than showing a
        broken image.
     3. Scroll arrows — revealed only when the track actually overflows (with
        3 reviews on desktop it doesn't, so they stay hidden).

   Registered as a custom element so the theme editor re-upgrades it for free
   on shopify:section:load.
   ========================================================================== */

(function () {
  'use strict';

  if (customElements.get('fr-google-reviews')) return;

  var CLAMP_CLASS = 'fr-gr__text--clamped';

  class FrGoogleReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.fr-gr__track');
      this.nav = this.querySelector('.fr-gr__nav');
      this.prev = this.querySelector('.fr-gr__arrow--prev');
      this.next = this.querySelector('.fr-gr__arrow--next');

      this.setUpRepeats();
      this.setUpAvatars();
      this.setUpClamps();
      this.setUpArrows();
      this.setUpLightbox();
    }

    /* ---- 0. repeated cards --------------------------------------------- */

    /* When the row is padded out by cycling a short review list, the copies are
       marked aria-hidden. An aria-hidden subtree must not contain anything
       focusable, or keyboard users tab into a region screen readers have been
       told to ignore — so take the copies' controls out of the tab order.
       Mouse clicks still work, which is why this isn't `inert`: a visible card
       that ignores clicks is worse than one you can't tab to. */
    setUpRepeats() {
      var repeats = this.querySelectorAll('[data-repeat="true"]');
      Array.prototype.forEach.call(repeats, function (item) {
        var focusable = item.querySelectorAll('a[href], button, [tabindex]');
        Array.prototype.forEach.call(focusable, function (el) {
          el.setAttribute('tabindex', '-1');
        });
      });
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.lbObserver) this.lbObserver.disconnect();
    }

    /* ---- 1. avatars ---------------------------------------------------- */

    setUpAvatars() {
      var imgs = this.querySelectorAll('img.fr-gr__avatar[data-initial]');
      Array.prototype.forEach.call(imgs, function (img) {
        var swap = function () {
          var chip = document.createElement('span');
          chip.className = 'fr-gr__avatar fr-gr__avatar--initial';
          chip.setAttribute('aria-hidden', 'true');
          chip.textContent = img.getAttribute('data-initial') || '★';
          if (img.parentNode) img.replaceWith(chip);
        };
        // Already failed before this script ran (cached 404, expired URL).
        if (img.complete && img.naturalWidth === 0) {
          swap();
          return;
        }
        img.addEventListener('error', swap, { once: true });
      });
    }

    /* ---- 2. read more -------------------------------------------------- */

    setUpClamps() {
      var self = this;
      var texts = this.querySelectorAll('.fr-gr__text');

      Array.prototype.forEach.call(texts, function (text, i) {
        // Measure the <p> itself, NOT this wrapper. -webkit-line-clamp clips the
        // paragraph, so the wrapper's scrollHeight just follows its clamped
        // child and would always look like "nothing hidden".
        var quote = text.querySelector('.fr-gr__quote');
        if (!quote) return;

        text.classList.add(CLAMP_CLASS);

        // Only offer the toggle if the clamp is actually hiding something.
        if (quote.scrollHeight <= quote.clientHeight + 1) {
          text.classList.remove(CLAMP_CLASS);
          return;
        }

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fr-gr__more';
        btn.textContent = 'Read more';
        btn.setAttribute('aria-expanded', 'false');

        // Point at the paragraph, since that's the bit that actually expands.
        var id = quote.id || 'fr-gr-quote-' + (self.dataset.uid || '') + i;
        quote.id = id;
        btn.setAttribute('aria-controls', id);

        // This button is created after setUpRepeats has run, so it needs the
        // same treatment: no tab stop inside an aria-hidden copy.
        if (text.closest('[data-repeat="true"]')) {
          btn.setAttribute('tabindex', '-1');
        }

        btn.addEventListener('click', function () {
          var expanded = text.classList.toggle(CLAMP_CLASS) === false;
          btn.textContent = expanded ? 'Show less' : 'Read more';
          btn.setAttribute('aria-expanded', String(expanded));
        });

        // Inside .fr-gr__text, not after it, so the compact style can park it
        // over the end of the last clamped line ("…high level of servic… Show
        // more") instead of spending a whole extra line on it. It sits outside
        // the clamped <p>, so the clamp never hides the button itself.
        text.appendChild(btn);
      });
    }

    /* ---- 3. arrows ----------------------------------------------------- */

    setUpArrows() {
      var self = this;
      if (!this.track || !this.nav || !this.prev || !this.next) return;

      var overflows = function () {
        return self.track.scrollWidth - self.track.clientWidth > 2;
      };

      var sync = function () {
        if (!overflows()) {
          self.nav.hidden = true;
          return;
        }
        self.nav.hidden = false;
        var max = self.track.scrollWidth - self.track.clientWidth;
        self.prev.disabled = self.track.scrollLeft <= 2;
        self.next.disabled = self.track.scrollLeft >= max - 2;
      };

      var page = function (dir) {
        var card = self.track.querySelector('.fr-gr__item');
        var step = card
          ? card.getBoundingClientRect().width + 16
          : self.track.clientWidth * 0.8;
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        self.track.scrollBy({
          left: dir * step,
          behavior: reduce ? 'auto' : 'smooth',
        });
      };

      this.prev.addEventListener('click', function () {
        page(-1);
      });
      this.next.addEventListener('click', function () {
        page(1);
      });
      this.track.addEventListener('scroll', sync, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(sync);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', sync);
      }

      sync();
    }

    /* ---- 4. lightbox --------------------------------------------------- */

    setUpLightbox() {
      var self = this;
      var lb = this.querySelector('.fr-gr__lb');
      if (!lb || typeof lb.showModal !== 'function') return; // no <dialog> → links still work

      this.lb = lb;
      this.lbTrack = lb.querySelector('.fr-gr__lb-track');
      this.lbAuthor = lb.querySelector('.fr-gr__lb-author');
      this.lbCounter = lb.querySelector('.fr-gr__lb-counter');
      this.lbPrev = lb.querySelector('.fr-gr__lb-arrow--prev');
      this.lbNext = lb.querySelector('.fr-gr__lb-arrow--next');

      // Delegate: every tile is an <a href> to the full image, so this is a
      // pure upgrade — without JS the link still opens the photo.
      this.addEventListener('click', function (e) {
        // Keep this list in step with the tile classes in
        // snippets/fr-google-review-card.liquid. The compact card's thumb is
        // .fr-gr__c-thumb, and leaving it out here meant a click sailed past the
        // lightbox and navigated to the bare .jpg instead.
        var tile = e.target.closest('.fr-gr__photo-link, .fr-gr__thumb, .fr-gr__c-thumb');
        if (!tile || !self.contains(tile)) return;
        // Let modified clicks (new tab, download) behave normally.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

        // Any [data-images] host works, so the summary card's combined strip
        // opens the same lightbox as an individual review's gallery.
        var host = tile.closest('[data-images]');
        if (!host) return;

        var shots;
        try {
          shots = JSON.parse(host.dataset.images);
        } catch (err) {
          return; // malformed data → fall through to the plain link
        }
        if (!shots || !shots.length) return;

        e.preventDefault();
        self.openLightbox(
          shots,
          parseInt(tile.dataset.index, 10) || 0,
          host.dataset.galleryTitle || ''
        );
      });

      lb.querySelector('.fr-gr__lb-close').addEventListener('click', function () {
        lb.close();
      });

      // Click the backdrop (outside the stage) to dismiss.
      lb.addEventListener('click', function (e) {
        if (e.target === lb) lb.close();
      });

      lb.addEventListener('close', function () {
        self.lbTrack.innerHTML = ''; // drop the big images from memory
      });

      lb.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          self.lbPage(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          self.lbPage(1);
        }
      });

      this.lbPrev.addEventListener('click', function () {
        self.lbPage(-1);
      });
      this.lbNext.addEventListener('click', function () {
        self.lbPage(1);
      });

      // Only adopt the scrolled-to index once scrolling has SETTLED. Reading it
      // on every scroll event meant a smooth arrow animation kept overwriting
      // lbIndex with intermediate positions, so a second arrow click computed
      // its target from a stale spot and went nowhere.
      this.lbTrack.addEventListener('scroll', function () {
        clearTimeout(self.lbScrollTimer);
        self.lbScrollTimer = setTimeout(function () {
          var w = self.lbTrack.clientWidth;
          if (!w) return;
          self.lbIndex = self.lbClamp(Math.round(self.lbTrack.scrollLeft / w));
          self.lbRender();
        }, 120);
      }, { passive: true });
    }

    openLightbox(shots, index, title) {
      var self = this;
      this.lbShots = shots;
      this.lbTrack.innerHTML = '';

      shots.forEach(function (shot, i) {
        var li = document.createElement('li');
        li.className = 'fr-gr__lb-item';
        var img = document.createElement('img');
        // Cap at 1600 — the masters are 1600px, so asking for more just upscales.
        // Responsive rather than always pulling the full 1600 (~560KB a photo):
        // a phone gets the 720 and saves most of that.
        img.src = shot.url + '&width=1600';
        img.srcset =
          shot.url + '&width=720 720w, ' +
          shot.url + '&width=1080 1080w, ' +
          shot.url + '&width=1600 1600w';
        img.sizes = '100vw';
        img.alt = shot.alt || '';
        // The first one must not be lazy or it can be blank on open.
        img.loading = i === 0 ? 'eager' : 'lazy';
        img.decoding = 'async';
        li.appendChild(img);
        self.lbTrack.appendChild(li);
      });

      this.lbAuthor.textContent = title || 'Customer photos';
      this.lb.showModal();

      // Jump to the clicked photo without animating from photo 1. Each slide is
      // exactly one track-width, so index * clientWidth is exact — offsetLeft
      // was measured against the nearest positioned ancestor (the dialog), not
      // the track, which landed us a slide out and read "2 of 5" on photo 1.
      this.lbIndex = this.lbClamp(index);
      this.lbTrack.scrollLeft = this.lbIndex * this.lbTrack.clientWidth;
      this.lbRender();
    }

    lbClamp(i) {
      var n = this.lbShots ? this.lbShots.length : 0;
      return Math.max(0, Math.min(i, n - 1));
    }

    /* Absolute target from a tracked index, never a relative scrollBy — two
       quick arrow clicks used to land on the same slide because the second
       scrollBy measured from a position the smooth animation hadn't reached. */
    lbPage(dir) {
      if (!this.lbShots || !this.lbShots.length) return;
      var i = this.lbClamp((this.lbIndex || 0) + dir);
      if (i === this.lbIndex) return;
      this.lbIndex = i;

      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.lbTrack.scrollTo({
        left: i * this.lbTrack.clientWidth,
        behavior: reduce ? 'auto' : 'smooth',
      });
      this.lbRender(); // update the label now, not when the animation lands
    }

    lbRender() {
      if (!this.lbShots || !this.lbShots.length) return;
      var i = this.lbIndex || 0;
      this.lbCounter.textContent = i + 1 + ' of ' + this.lbShots.length;
      this.lbPrev.disabled = i === 0;
      this.lbNext.disabled = i === this.lbShots.length - 1;
    }
  }

  customElements.define('fr-google-reviews', FrGoogleReviews);
})();
